import React, { useCallback, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, Box, InputAdornment, FormLabel, useTheme, useMediaQuery } from '@mui/material';
import { AccountCircle, Lock } from '@mui/icons-material';
import { Loader, useStateContext } from '@vegi.toenlioe/novitate-react-components';
import Menu from '../models/Menu';

interface IFormInput {
	email: string;
	password: string;
}

const Login: React.FC = () => {
    const { register, handleSubmit, formState: { errors } } = useForm<IFormInput>();
    const navigate = useNavigate();
    const {apiCaller, setAuthState} = useStateContext();
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isMd = useMediaQuery(theme.breakpoints.down('md'));

    const onSubmit: SubmitHandler<IFormInput> = useCallback((data) => {
        if (!data.email || !data.password)
        {
            alert("Username dan password harus diisi.");
            return;   
        }

        if (!apiCaller)
            return;

        setLoading(true);

        apiCaller.post(`user/karyawan/login`, {
            username: data.email,
            password: data.password,
        })
            .then((response) => {
                const {data: {data: responseData}} = response;
                
                setAuthState({
                    type: 'login',
                    username: responseData.username,
                    nama_karyawan: responseData.namaKaryawan,
                    kode_divisi: responseData.namaDivisi,
                    token: responseData.token,
                    daftar_menu: responseData.menuList.map((m: Menu) => ({
                        kode_menu: m.kode,
                        nama_menu: m.nama,
                        can_view: true,
                        can_add: true,
                        can_edit: true,
                        can_delete: true,
                        see_other_div: true,
                    })),
                });
                setLoading(false);
                navigate('/dashboard');
            })
            .catch((error) => {
                console.error((error));
                setLoading(false);
            })
        ;
    }, [apiCaller, setAuthState, navigate]);

    return (
        <>
        {
            loading && <Loader />
        }
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '100vh',
                    '@supports (height: 100dvh)': {
                        height: "100dvh",
                    },
                    width: '100vw',
                    '@supports (width: 100dvw)': {
                        width: "100dvw",
                    },
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "30vh",
                        '@supports (height: 30vh)': {
                            height: "30vh",
                        },
                    }}
                >
                    <FormLabel
                        sx={{
                            fontSize: isMobile || isMd ? "2.2rem" : "4rem",
                            fontWeight: "600",
                            color: "#f36e2b",
                            textAlign: "center",
                            width: "100%",
                        }}
                    >
                        Japfa Comfeed
                    </FormLabel>
                    <FormLabel
                        sx={{
                            fontSize: isMobile || isMd ? "1.3rem" : "3rem",
                            fontWeight: "400",
                            color: "#f36e2b",
                            textAlign: "center",
                            width: "100%",
                        }}
                    >
                        Web Admin Koperasi
                    </FormLabel>
                </Box>
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "start",
                        height: "70vh",
                        '@supports (height: 70vh)': {
                            height: "70vh",
                        },
                        paddingTop: "3rem",
                    }}
                >
                    <form className="login-form" onSubmit={handleSubmit(onSubmit)}>
                        <div className="card mb-0">
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label">Email Address</label>
                                    <div className="form-control-feedback form-control-feedback-start">
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            id="email"
                                            placeholder="Email Address"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AccountCircle className="ph-user-circle text-muted" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        {
                                            ...register('email', {
                                                required: 'Email is required',
                                                pattern: {
                                                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                                                    message: 'Invalid email address',
                                                },
                                            })
                                        }
                                            error={!!errors.email}
                                            helperText={errors.email ? errors.email.message : ''}
                                            autoComplete='off'
                                        />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Password</label>
                                    <div className="form-control-feedback form-control-feedback-start">
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            id="password"
                                            type="password"
                                            placeholder="Password"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Lock className="ph-lock text-muted" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        {
                                            ...register('password', {
                                                required: 'Password is required',
                                                minLength: {
                                                    value: 6,
                                                    message: 'Password must be at least 6 characters',
                                                },
                                            })
                                        }
                                            error={!!errors.password}
                                            helperText={errors.password ? errors.password.message : ''}
                                        />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        className="btn bg-color-default text-white w-100"
                                    >
                                        Sign in
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </form>
                </Box>
            </Box>
        </>
    );
};

export default Login;
